import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useT } from "@/i18n/I18nContext";
import { toast } from "sonner";
import { FileText, Download, Calendar, Car as CarIcon, Loader2, Send, MessageCircle, Mail, Copy, X } from "lucide-react";
import { format, parseISO } from "date-fns";

/**
 * Lists every signed contract stored under a customer. Each item is a
 * stored PDF (via /api/customers/{id}/contracts) with a link to view/download
 * + Share button (WhatsApp / Email / Copy public link).
 */
export default function CustomerContracts({ customerId }) {
  const { t, fmtCurrency } = useT();
  const [items, setItems] = useState(null);
  const [shareFor, setShareFor] = useState(null);
  const [share, setShare] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    if (!customerId) return;
    try {
      const { data } = await api.get(`/customers/${customerId}/contracts`);
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setItems([]);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  const downloadBlob = async (file) => {
    try {
      const res = await api.get(`/files/${file.id}/raw`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = file.original_filename || "contract.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const viewBlob = async (file) => {
    try {
      const res = await api.get(`/files/${file.id}/raw`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const openShare = async (file) => {
    setShareFor(file);
    setShare(null);
    setCreating(true);
    try {
      const { data } = await api.post(`/files/${file.id}/share`, {
        origin_url: window.location.origin,
      });
      setShare(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setShareFor(null);
    } finally {
      setCreating(false);
    }
  };

  const closeShare = () => {
    setShareFor(null);
    setShare(null);
  };

  const copyLink = async () => {
    if (!share?.url) return;
    try {
      await navigator.clipboard.writeText(share.url);
      toast.success(t("share.linkCopied"));
    } catch { /* ignore */ }
  };

  if (items === null) {
    return (
      <div className="flex items-center gap-2 text-xs text-zinc-500">
        <Loader2 className="w-3 h-3 animate-spin" /> {t("common.loading")}
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid={`customer-contracts-${customerId}`}>
      <div>
        <div className="overline flex items-center gap-2">
          <FileText className="w-3 h-3 text-[#F5A623]" /> {t("contracts.title")}
        </div>
        <p className="text-xs text-zinc-500 mt-1">{t("contracts.subtitle")}</p>
      </div>

      {items.length === 0 ? (
        <div className="border border-zinc-200 p-6 text-center bg-white">
          <FileText className="w-8 h-8 mx-auto text-zinc-300 mb-2" />
          <div className="text-xs text-zinc-500">{t("contracts.empty")}</div>
        </div>
      ) : (
        <div className="border border-zinc-200 bg-white divide-y divide-zinc-100">
          {items.map((f) => (
            <div key={f.id} className="flex items-center justify-between gap-3 px-3 py-2.5 hover:bg-zinc-50" data-testid={`contract-row-${f.id}`}>
              <div className="flex items-start gap-3 min-w-0">
                <div className="bg-[#F5A623]/10 text-[#D97706] p-1.5 mt-0.5">
                  <FileText className="w-4 h-4" />
                </div>
                <div className="min-w-0">
                  <div className="font-mono text-xs font-semibold truncate">
                    {f.original_filename}
                  </div>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[10px] text-zinc-500 mt-0.5">
                    <span className="inline-flex items-center gap-1">
                      <Calendar className="w-3 h-3" /> {format(parseISO(f.created_at), "MM/dd/yyyy HH:mm")}
                    </span>
                    {f.car && (
                      <span className="inline-flex items-center gap-1">
                        <CarIcon className="w-3 h-3" /> {f.car.model} · {f.car.plate}
                      </span>
                    )}
                    {f.rental?.total && (
                      <span className="font-semibold text-zinc-700">{fmtCurrency(f.rental.total)}</span>
                    )}
                    <span className="uppercase tracking-wider bg-zinc-100 px-1.5">{f.lang || "en"}</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <button
                  type="button"
                  onClick={() => viewBlob(f)}
                  className="text-xs border border-zinc-300 hover:border-[#F5A623] hover:text-[#D97706] px-2 py-1 inline-flex items-center gap-1"
                  data-testid={`contract-view-${f.id}`}
                >
                  <FileText className="w-3 h-3" /> {t("contracts.view")}
                </button>
                <button
                  type="button"
                  onClick={() => downloadBlob(f)}
                  className="text-xs border border-zinc-300 hover:border-black hover:text-black px-2 py-1 inline-flex items-center gap-1"
                  data-testid={`contract-download-${f.id}`}
                >
                  <Download className="w-3 h-3" /> {t("contracts.download")}
                </button>
                <button
                  type="button"
                  onClick={() => openShare(f)}
                  className="text-xs bg-[#F5A623] hover:bg-[#D97706] text-white px-2 py-1 inline-flex items-center gap-1"
                  data-testid={`contract-share-${f.id}`}
                >
                  <Send className="w-3 h-3" /> {t("contracts.send")}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {shareFor && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={closeShare} data-testid="share-modal">
          <div className="bg-white max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-3 border-b border-zinc-200 bg-zinc-50 flex items-center justify-between">
              <div>
                <div className="overline">{t("share.title")}</div>
                <div className="text-sm font-display font-bold tracking-tight">{shareFor.original_filename}</div>
              </div>
              <button onClick={closeShare} className="p-1.5 border border-zinc-200 hover:border-zinc-400" aria-label="close">
                <X className="w-4 h-4" />
              </button>
            </div>

            {creating || !share ? (
              <div className="p-8 text-center text-sm text-zinc-500">
                <Loader2 className="w-5 h-5 mx-auto animate-spin mb-2 text-[#F5A623]" /> {t("share.generating")}
              </div>
            ) : (
              <div className="p-5 space-y-4">
                <div className="border border-zinc-200 bg-zinc-50 p-3">
                  <div className="overline mb-1">{t("share.publicLink")}</div>
                  <div className="flex items-center gap-2">
                    <input
                      readOnly
                      value={share.url}
                      className="flex-1 bg-white border border-zinc-200 px-2 py-1.5 text-xs font-mono"
                      onClick={(e) => e.target.select()}
                      data-testid="share-link-input"
                    />
                    <button
                      onClick={copyLink}
                      className="border border-zinc-300 hover:border-[#F5A623] hover:text-[#D97706] px-2 py-1.5 inline-flex items-center gap-1 text-xs"
                      data-testid="share-copy-link"
                    >
                      <Copy className="w-3.5 h-3.5" /> {t("share.copy")}
                    </button>
                  </div>
                  <div className="text-[10px] text-zinc-500 mt-1.5 font-mono">
                    {t("share.expiresIn", { days: share.ttl_days })}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {share.whatsapp_url ? (
                    <a
                      href={share.whatsapp_url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1FAE54] text-white px-3 py-2.5 text-sm transition-colors"
                      data-testid="share-whatsapp"
                    >
                      <MessageCircle className="w-4 h-4" /> {t("share.viaWhatsapp")}
                    </a>
                  ) : (
                    <div className="border border-zinc-200 px-3 py-2.5 text-xs text-zinc-500 text-center">
                      {t("share.noPhone")}
                    </div>
                  )}
                  {share.mailto_url ? (
                    <a
                      href={share.mailto_url}
                      className="inline-flex items-center justify-center gap-2 bg-black hover:bg-zinc-800 text-white px-3 py-2.5 text-sm transition-colors"
                      data-testid="share-email"
                    >
                      <Mail className="w-4 h-4" /> {t("share.viaEmail")}
                    </a>
                  ) : (
                    <div className="border border-zinc-200 px-3 py-2.5 text-xs text-zinc-500 text-center">
                      {t("share.noEmail")}
                    </div>
                  )}
                </div>

                {share.whatsapp_text && (
                  <div>
                    <div className="overline mb-1">{t("share.previewMessage")}</div>
                    <pre className="text-xs bg-zinc-50 border border-zinc-200 p-2.5 whitespace-pre-wrap font-mono text-zinc-700 max-h-32 overflow-y-auto">{share.whatsapp_text}</pre>
                  </div>
                )}

                <div className="text-[10px] text-zinc-500 border-t border-zinc-100 pt-3">
                  {t("share.securityNote")}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
